import React, { useState, useEffect } from 'react';
import { versionesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const VersionControl = ({ archivoId, onVersionCreated, onClose }) => {
  const { user } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  const [versiones, setVersiones] = useState([]);
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar versiones del archivo
  const loadVersiones = async () => {
    try {
      setLoading(true);
      const response = await versionesAPI.getVersions(archivoId);
      setVersiones(response.data.versiones || []);
      setArchivo(response.data.archivo);
    } catch (err) {
      setError('Error al cargar las versiones');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadVersiones();
  }, [archivoId]);


  // Revertir a una versión anterior
  const handleRevertVersion = async (versionId) => {
    if (!await showConfirm('¿Estás seguro de que quieres revertir a esta versión? Se creará una nueva versión basada en esta.')) {
      return;
    }

    try {
      await versionesAPI.revertVersion(archivoId, versionId);
      await loadVersiones();
      showSuccess('Versión revertida exitosamente');
    } catch (err) {
      showError('Error al revertir versión: ' + (err.response?.data?.error || err.message));
    }
  };

  // Descargar versión
  const handleDownloadVersion = async (versionId, nombreOriginal) => {
    try {
      const response = await versionesAPI.downloadVersion(archivoId, versionId);
      
      // Crear blob y descargar
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreOriginal;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showError('Error al descargar versión: ' + (err.response?.data?.error || err.message));
    }
  };

  // Eliminar versión
  const handleDeleteVersion = async (versionId) => {
    if (!await showConfirm('¿Estás seguro de que quieres eliminar esta versión? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await versionesAPI.deleteVersion(archivoId, versionId);
      await loadVersiones();
      showSuccess('Versión eliminada exitosamente');
    } catch (err) {
      showError('Error al eliminar versión: ' + (err.response?.data?.error || err.message));
    }
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener icono según tipo de archivo
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      txt: '📄',
      zip: '📦',
      rar: '📦',
      dwg: '🏗️'
    };
    return iconMap[extension] || '📄';
  };

  if (loading) {
    return (
      <div className="version-control-modal">
        <div className="modal-content">
          <div className="loading">Cargando versiones...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="version-control-modal">
        <div className="modal-content">
          <div className="error">{error}</div>
          <button onClick={onClose} className="btn btn-secondary">Cerrar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="version-control-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h3>📁 Control de Versiones</h3>
          <button onClick={onClose} className="btn-close">&times;</button>
        </div>

        {archivo && (
          <div className="archivo-info">
            <h4>{getFileIcon(archivo.nombreOriginal)} {archivo.nombreOriginal}</h4>
            <p><strong>Versión actual:</strong> V{archivo.versionActual}</p>
            <div className="estadisticas">
              <span className="badge badge-info">
                {versiones.length} versión{versiones.length !== 1 ? 'es' : ''}
              </span>
              {versiones.length >= 3 && (
                <span className="badge badge-warning">Límite alcanzado</span>
              )}
            </div>
          </div>
        )}


        {/* Lista de versiones */}
        <div className="versiones-list">
          <h4>📋 Historial de Versiones</h4>
          {versiones.length === 0 ? (
            <div className="no-versions">
              <p>No hay versiones registradas para este archivo.</p>
            </div>
          ) : (
            <div className="versiones-grid">
              {versiones.map((version) => (
                <div key={version.id} className={`version-card ${version.version === archivo?.versionActual ? 'current' : ''}`}>
                  <div className="version-header">
                    <div className="version-info">
                      <h5>V{version.version}</h5>
                      <p className="version-date">{formatDate(version.fechaCreacion)}</p>
                      <p className="version-user">Por: {version.usuario.nombre}</p>
                    </div>
                    {version.version === archivo?.versionActual && (
                      <span className="badge badge-success">Actual</span>
                    )}
                  </div>
                  
                  <div className="version-actions">
                    <button
                      onClick={() => handleDownloadVersion(version.id, version.nombreOriginal)}
                      className="btn btn-sm btn-primary"
                      title="Descargar versión"
                    >
                      📥 Descargar
                    </button>
                    
                    {(user?.rol === 'JEFE' || user?.rol === 'ADMIN') && (
                      <>
                        <button
                          onClick={() => handleRevertVersion(version.id)}
                          className="btn btn-sm btn-warning"
                          title="Revertir a esta versión"
                          disabled={version.version === archivo?.versionActual}
                        >
                          🔄 Revertir
                        </button>
                        
                        {version.version !== archivo?.versionActual && (
                          <button
                            onClick={() => handleDeleteVersion(version.id)}
                            className="btn btn-sm btn-danger"
                            title="Eliminar versión"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default VersionControl;
